import { Button, Input, Select, Table } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import { smeApplicationStateReset } from '../../../features/smeApplication/smeApplicationSlice';
import { numberWithCommas } from '../../../helpers/number';
import { getSmeApplicationsListingThunk } from '../../../services/smeApplicationService/smeApplicationThunk';
import { AppDispatch, RootState } from '../../../store/store';
import { displayDate, displayTime } from '../../../utils/datetime.util';

import type { SmeApplication } from '../../../features/smeApplication/smeApplicationSlice';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { SmeAgreementStatusEnum } from '../../../enum/agreementStepStatus';
import { showFinalStatus } from '../../../utils/string.util';

const { Search } = Input;

const SmeCustomerServiceSearchApplicationPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const { smeApplicationsList } = useSelector((state: RootState) => state.smeApplication);

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {},
      filters: {},
    }),
    []
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<SmeApplication>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [searchState, setSearchState] = useState(initialParams.search);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);
  const [searchFiled, setSearchFiled] = useState('applicationNo');
  const [searchValue, setSearchValue] = useState('');

  const tablePagination = {
    total: smeApplicationsList.data.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Application Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => a.createdAt - b.createdAt,
      sortOrder: sortedInfo.columnKey === 'createdAt' ? sortedInfo.order : null,
      render: (text, record) => displayDate(text ?? record.createdAt) ?? '-',
    },
    {
      title: 'Application No.',
      dataIndex: 'applicationNo',
      key: 'applicationNo',
      render: (text, record) => (text ? text : '-'),
    },
    {
      title: 'Applicant Name',
      render: (text, record) =>
        record?.smeStaffApplication?.name ??
        record?.smeIndividualApplication?.name ??
        record?.smeSolePropApplication?.name ??
        record?.smePartnershipApplication?.name ??
        record?.smeCompanyApplication?.companyName ??
        '-',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'nric',
      key: 'nric',
      render: (text, record) =>
        record?.smeStaffApplication?.nric ??
        record?.smeIndividualApplication?.nric ??
        record?.smeSolePropApplication?.nric ??
        record?.smePartnershipApplication?.nric ??
        '-',
    },
    {
      title: 'Status',
      render: (text, record) => {
        if (!record) return '-';

        if (record.status === 'DRAFT') {
          return 'Draft_Application';
        }

        if (record.status === 'SUBMITTED_FOR_VERIFIER_2') {
          return 'SUBMITTED FOR VERIFIER 2';
        }

        if (record.status === 'APPROVED' && !record.smeAgreement) {
          return 'APPROVED';
        }

        if (record.smeAgreement) {
          const agreementStatus = record.smeAgreement.status;
          return showFinalStatus(record.status, agreementStatus) ?? '-';
        }

        return showFinalStatus(record.status, null) ?? '-';
      },
    },
    {
      title: 'Agreement Date',
      dataIndex: 'signDate',
      key: 'signDate',
      render: (text, record) => {
        if (record?.smeAgreement?.signDate) {
          return displayDate(record?.smeAgreement.signDate);
        }
        return '-';
      },
    },
    {
      title: 'Stamp Certificate',
      dataIndex: 'eStamp',
      key: 'eStamp',
      render: (text, record) => {
        if (!record?.smeAgreement) {
          return '-';
        }

        const agreement = record.smeAgreement;

        if (
          agreement.status === SmeAgreementStatusEnum.PENDING_DISBURSEMENT ||
          agreement.status === SmeAgreementStatusEnum.DISBURSED
        ) {
          if (!agreement.eStampFileId) {
            return 'Pending';
          }
          if (agreement.clickedDownloadEStampingCert) {
            return agreement.approveUploadedEStampingCert ? 'Approved' : 'Uploaded';
          }
        }
        return '-';
      },
    },
    {
      title: 'Applied Loan Amount',
      dataIndex: 'appliedLoanAmount',
      key: 'appliedLoanAmount',
      render: (text) => (text ? `RM ${numberWithCommas(text)}` : '-'),
    },
    {
      title: 'Approved Loan Amount',
      dataIndex: 'financeAmount',
      key: 'financeAmount',
      render: (text, record) =>
        record.status === 'APPROVED' ? (text != null ? `RM ${numberWithCommas(text)}` : '-') : '-',
    },
  ];

  const fetchSmeApplications = async (params?: any) => {
    await dispatch(getSmeApplicationsListingThunk(params));
  };

  const resetSmeApplicationState = async () => {
    await dispatch(smeApplicationStateReset());
  };

  const onSearch = (value: string) => {
    const trimmedValue = value.trim();
    fetchSmeApplications({
      ...initialParams,
      filters: filterState,
      search: { [searchFiled]: trimmedValue.toUpperCase() },
    });
    setSearchState({ [searchFiled]: trimmedValue });
    setSearchValue(trimmedValue);
  };

  const handleTableChange: TableProps<SmeApplication>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      searchValue.length === 0
        ? {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
            search: {
              [searchFiled]: '',
            },
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
            search: {
              [searchFiled]: searchValue,
            },
          };

    fetchSmeApplications(param);
    setSortedInfo(sorter as SorterResult<SmeApplication>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(() => {
    resetSmeApplicationState();

    const params = {
      ...initialParams,
      filters: filterState,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
      search: {
        applicationNo: '',
      },
    };
    console.log(params);
    fetchSmeApplications(params);
  });

  return (
    <>
      <Helmet>
        <title>Search SME Application - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Search SME Application" />
      <Input.Group compact>
        <Select
          defaultValue={searchFiled}
          style={{ width: 200 }}
          onChange={(value) => setSearchFiled(value)}
          options={[
            { value: 'applicationNo', label: 'Application No' },
            { value: 'name', label: 'Applicant Name' },
            { value: 'nric', label: 'Applicant NRIC' },
            { value: 'staffId', label: 'Staff ID' },
            { value: 'redOneUsername', label: 'Partner ID' },
          ]}
        />
        <Search placeholder="Input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={smeApplicationsList?.data?.data}
        loading={smeApplicationsList.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default SmeCustomerServiceSearchApplicationPage;
